import React from "react";
import studioData from "../data/studioData.json";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

function buildWhatsAppLink(phone, message) {
  const whatsappNumber = phone?.replace(/\D/g, "") ?? "";
  if (!whatsappNumber) return "";
  const encoded = encodeURIComponent(message ?? "");
  return encoded
    ? `https://wa.me/${whatsappNumber}?text=${encoded}`
    : `https://wa.me/${whatsappNumber}`;
}

function PackageIncludes({ includes }) {
  if (!includes.length) return null;

  return (
    <div className="mt-5 space-y-2 border-t border-white/15 pt-4 text-sm leading-relaxed text-text-secondary">
      {includes.map((line) => (
        <p key={line} className="flex gap-2">
          <span className="text-white">/</span>
          <span>{line}</span>
        </p>
      ))}
    </div>
  );
}

const InteriorPackages = () => {
  const { phone } = studioData.studioProfile ?? {};
  const content = studioData.siteContent?.interiorPackages ?? {};
  const packages = Array.isArray(studioData.interiorPackages) ? studioData.interiorPackages : [];
  const journey = Array.isArray(studioData.interiorJourney) ? studioData.interiorJourney : [];
  const startWhatsApp = buildWhatsAppLink(phone, content.startWhatsappMessage);
  const heroPackage = packages.find((item) => item.featured) ?? packages[0];
  const supportingPackages = packages.filter((item) => item.id !== heroPackage?.id);

  return (
    <section id="interior-packages" className="relative z-10 mx-auto w-full max-w-6xl px-6 py-12 md:py-16">
      <div className="mb-8 max-w-3xl">
        <p className="section-kicker">{content.kicker ?? "03 Packages"}</p>
        <h2 className="section-title mt-3 uppercase">
          {content.title ?? "Not sure what your home needs?"}
        </h2>
        <p className="mt-4 text-2xl font-medium leading-tight tracking-[-0.02em] text-white md:text-3xl">
          {content.subtitle ?? "Choose how much help you want."}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-text-secondary md:text-base">
          {content.intro ??
            "From a first conversation to a complete interior design, choose the level of help that suits your home."}
        </p>
      </div>

      <div className="package-discovery mb-8">
        <div className="min-w-0 flex-1">
          <p className="section-kicker">{content.startHeadline ?? "Prefer to talk first?"}</p>
          <h3 className="mt-2 text-xl font-medium tracking-[-0.01em] text-white md:text-2xl">
            {content.startTitle ?? "Free 15-Minute Discovery Call"}
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-secondary">
            {content.startDescription ??
              "Tell us about your home, what you’re struggling with, and what you’d like to achieve. We’ll recommend the right next step."}
          </p>
        </div>
        {startWhatsApp && (
          <a
            href={startWhatsApp}
            className="outline-link mt-4 inline-flex shrink-0 items-center gap-2 px-5 md:mt-0"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={content.startCtaLabel ?? "Book a free call"}
          >
            <FontAwesomeIcon icon={faWhatsapp} />
            <span>{content.startCtaLabel ?? "Book a free call"}</span>
          </a>
        )}
      </div>

      {journey.length > 0 && (
        <div className="mb-8" aria-label="Service journey">
          <p className="mb-4 text-center text-sm text-text-secondary">
            {content.journeyCaption}
          </p>
          <ol className="package-journey">
            {journey.map((step, index) => {
              const isHero = step.id === heroPackage?.id;
              return (
                <li key={step.id} className="package-journey-item">
                  {index > 0 && (
                    <span className="package-journey-arrow" aria-hidden="true">
                      →
                    </span>
                  )}
                  <div className={`package-journey-step ${isHero ? "package-journey-step-in-home" : ""}`}>
                    <span className="package-journey-label">{step.label}</span>
                    {step.caption && (
                      <span className="package-journey-caption">{step.caption}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {heroPackage && (
        <article className="glass-panel package-hero mb-5 p-6 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.35fr_0.65fr] lg:items-end">
            <div>
              <p className="section-kicker">{content.heroKicker ?? "Start here"}</p>
              <p className="mt-4 text-sm uppercase tracking-[0.16em] text-text-secondary">
                {heroPackage.durationOrScope}
              </p>
              <h3 className="mt-2 text-3xl font-medium leading-tight tracking-[-0.02em] text-white md:text-5xl">
                {heroPackage.name}
              </h3>
              <p className="mt-4 text-4xl font-medium tracking-[-0.03em] text-white md:text-5xl">
                {heroPackage.price}
              </p>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-text-secondary md:text-base">
                {heroPackage.summary}
              </p>
            </div>
            <div>
              <PackageIncludes includes={Array.isArray(heroPackage.includes) ? heroPackage.includes : []} />
              {buildWhatsAppLink(phone, heroPackage.whatsappMessage) && (
                <a
                  href={buildWhatsAppLink(phone, heroPackage.whatsappMessage)}
                  className="glass-button mt-6 inline-flex w-full items-center justify-center gap-2 px-6"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${content.heroCtaLabel ?? "Book an in-home visit"}: ${heroPackage.name}`}
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                  <span>{content.heroCtaLabel ?? "Book an in-home visit"}</span>
                </a>
              )}
            </div>
          </div>
        </article>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {supportingPackages.map((item) => {
          const enquireLink = buildWhatsAppLink(phone, item.whatsappMessage);
          const includes = Array.isArray(item.includes) ? item.includes : [];

          return (
            <article key={item.id} className="glass-card flex h-full flex-col p-6">
              <p className="section-kicker !mb-3">{item.durationOrScope}</p>
              <h3 className="text-2xl font-medium leading-tight text-white">{item.name}</h3>
              <p className="mt-3 text-3xl font-medium tracking-[-0.02em] text-white">{item.price}</p>
              {item.priceNote && (
                <p className="mt-1 text-xs leading-relaxed text-text-secondary">{item.priceNote}</p>
              )}
              <p className="mt-4 text-sm leading-relaxed text-text-secondary">{item.summary}</p>
              <PackageIncludes includes={includes} />
              {enquireLink && (
                <div className="mt-auto pt-5">
                  <a
                    href={enquireLink}
                    className="outline-link inline-flex w-full items-center justify-center gap-2 px-4"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${content.enquireLabel ?? "Enquire on WhatsApp"}: ${item.name}`}
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                    <span>{content.enquireLabel ?? "Enquire on WhatsApp"}</span>
                  </a>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default InteriorPackages;
